import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { FieldLabel } from '@/settings/shared/section-card';
import type {
  PermissionGroup,
  PermissionResults,
  Role,
  RolesListItem,
  SystemPermission,
} from '@/types/settings-api';
import { useRoleById, useUpdateRole } from '@/settings/services';

/**
 * Role edit dialog — Flutter parity.
 *
 * Renders the role's own `permissiongrp` as a single scrollable list with
 * group headers (NOT 7 tabs). Mirrors Flutter `edit_role_widget.dart:226-460`
 * where the `TabController(length: 7)` defined at line 49 is declared but
 * NEVER USED — the actual rendering is a flat `ListView.builder` over
 * `roleData.permissiongrp`. (I initially shipped a 7-tab UI based on the
 * unused TabController. Refactored 2026-05-12 against the actual render.)
 *
 * Behaviour-locked details (all from `.dart` source, NOT inferred):
 *
 * - Column order: **Add / Edit / View / Delete** (`edit_role_widget.dart:188-216`).
 * - Only render permissions where `isAllowed === true`
 *   (`edit_role_widget.dart:233-240`).
 * - Skip groups whose entire `systemPermission[]` is filtered out (same line).
 * - Administrator lock: when `role.tenantId === 1` AND the permission key
 *   is one of {Manage_Organization_Details, Manage_Practice_Details,
 *   Manage_Doctors, Manage_Staff, Manage_Roles_Permission}, clicking ANY of
 *   the 4 checkboxes shows error "Organization access cant be changed for
 *   Administrator" and does nothing else (`edit_role_widget.dart:317-332`).
 * - When a permission has no prior `permissionResults`, clicking creates one
 *   with `id: null` and the toggled flag true (others false). Preserves the
 *   existing `permissionResults.id` (backend PK like 37333) for permissions
 *   that DO have prior results — losing it would duplicate-insert on save.
 *
 * Save: live `PUT /api/TenantRoles/{id}` via `useUpdateRole`. Body shape
 * verified against dev cURL — full Role JSON with the role's own permission
 * tree, no merge with the global catalogue.
 */
export interface RoleEditDialogProps {
  role: RolesListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
}

type PermissionEditState = Map<number, PermissionResults>;

const ADMIN_LOCKED_KEYS = new Set([
  'Manage_Organization_Details',
  'Manage_Practice_Details',
  'Manage_Doctors',
  'Manage_Staff',
  'Manage_Roles_Permission',
]);

export function RoleEditDialog({ role, open, onOpenChange, canEdit }: RoleEditDialogProps) {
  const { data: roleDetail, isLoading: roleLoading } = useRoleById({
    roleId: role?.roleId,
    tenantId: role?.tenantId,
    organizationId: role?.organizationId,
  });
  const updateRole = useUpdateRole();

  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [edits, setEdits] = useState<PermissionEditState>(() => new Map());

  useEffect(() => {
    if (!open) return;
    setEditedName(roleDetail?.roleName ?? role?.roleName ?? '');
    setEditedDescription(roleDetail?.roleDescription ?? role?.roleDescription ?? '');
    setEdits(initialEditsFromRole(roleDetail ?? null));
  }, [open, role?.roleName, role?.roleDescription, roleDetail]);

  const isAdministratorRole = roleDetail?.tenantId === 1;

  const togglePermission = (
    permission: SystemPermission,
    key: 'add' | 'edit' | 'view' | 'delete',
  ) => {
    if (permission.id == null) return;
    if (isAdministratorRole && permission.permissionKey && ADMIN_LOCKED_KEYS.has(permission.permissionKey)) {
      toast.error("Organization access can't be changed for Administrator");
      return;
    }
    setEdits((prev) => {
      const updated = new Map(prev);
      const current = updated.get(permission.id!) ?? {
        id: permission.permissionResults?.id ?? null,
        add: false,
        edit: false,
        view: false,
        delete: false,
      };
      updated.set(permission.id!, { ...current, [key]: !current[key] });
      return updated;
    });
  };

  const getResults = (permission: SystemPermission): PermissionResults => {
    if (permission.id == null) {
      return { add: false, edit: false, view: false, delete: false };
    }
    return (
      edits.get(permission.id) ?? {
        id: permission.permissionResults?.id ?? null,
        add: false,
        edit: false,
        view: false,
        delete: false,
      }
    );
  };

  const isDirty = useMemo(() => {
    if (!roleDetail) return false;
    if (editedName !== (roleDetail.roleName ?? '')) return true;
    if (editedDescription !== (roleDetail.roleDescription ?? '')) return true;
    const original = initialEditsFromRole(roleDetail);
    if (original.size !== edits.size) return true;
    for (const [id, results] of edits) {
      if (!resultsEqual(results, original.get(id))) return true;
    }
    return false;
  }, [edits, editedName, editedDescription, roleDetail]);

  const isNameValid = editedName.trim().length >= 3;

  const handleSave = () => {
    if (!roleDetail) return;
    if (!isDirty) {
      onOpenChange(false);
      return;
    }
    if (!isNameValid) {
      toast.error('Please enter a valid role name (3+ characters).');
      return;
    }
    if (roleDetail.tenantId == null || !roleDetail.organizationId) {
      toast.error('Cannot save — missing tenant or organisation.');
      return;
    }

    const payload = buildSaveRolePayload(roleDetail, edits, editedName, editedDescription);

    updateRole.mutate(
      {
        role: payload,
        tenantId: roleDetail.tenantId,
        organizationId: roleDetail.organizationId,
      },
      {
        onSuccess: () => {
          toast.success(`Saved ${payload.roleName ?? 'role'}`);
          onOpenChange(false);
        },
        onError: (err) => {
          const message = err instanceof Error ? err.message : 'Failed to save role';
          toast.error(message);
        },
      },
    );
  };

  const handleCancel = () => onOpenChange(false);

  const visibleGroups = (roleDetail?.permissiongrp ?? [])
    .map((group) => ({
      group,
      visiblePermissions:
        group.systemPermission?.filter((p) => p.isAllowed && p.id != null) ?? [],
    }))
    .filter((g) => g.visiblePermissions.length > 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="!max-w-[720px] !w-full sm:!max-w-[720px] flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-black/5">
          <SheetTitle>Edit Role</SheetTitle>
          <SheetDescription>
            {role?.roleName ?? 'Role'} — manage role details and permission access.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-6 py-6 space-y-6 scrollbar-hide">
          <div className="space-y-4">
            <div>
              <FieldLabel>Role name</FieldLabel>
              <Input
                value={editedName}
                maxLength={50}
                placeholder="Enter role name"
                disabled={!canEdit}
                onChange={(e) => setEditedName(e.target.value)}
              />
              {canEdit && editedName.length > 0 && !isNameValid && (
                <p className="mt-1 text-sm text-[var(--color-error,#CD3232)]">
                  Please enter a valid role name (3+ characters).
                </p>
              )}
            </div>
            <div>
              <FieldLabel>Description</FieldLabel>
              <Input
                value={editedDescription}
                maxLength={250}
                placeholder="Enter description"
                disabled={!canEdit}
                onChange={(e) => setEditedDescription(e.target.value)}
              />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-medium text-[var(--color-text-muted)] mb-1">
              Permissions
            </h3>
            <p className="text-sm text-[var(--color-text-muted)] mb-4">
              Every role has a predefined set of permissions based on Scrips' industry knowledge,
              however you are free to assign them yourself.
            </p>

            {roleLoading ? (
              <div className="text-sm text-[var(--color-text-muted)] py-4">
                Loading permissions…
              </div>
            ) : visibleGroups.length === 0 ? (
              <div className="text-sm text-[var(--color-text-muted)] py-4">
                No permissions configured for this role.
              </div>
            ) : (
              <div className="space-y-6">
                <MatrixColumnHeader />
                {visibleGroups.map(({ group, visiblePermissions }) => (
                  <PermissionGroupSection
                    key={group.groupId ?? group.groupName ?? Math.random()}
                    group={group}
                    permissions={visiblePermissions}
                    canEdit={canEdit}
                    isAdministratorRole={isAdministratorRole}
                    getResults={getResults}
                    onToggle={togglePermission}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <SheetFooter className="px-6 py-4 border-t border-black/5 flex-row justify-end gap-2">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          {canEdit && (
            <Button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || updateRole.isPending || !isNameValid}
            >
              {updateRole.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function MatrixColumnHeader() {
  return (
    <div className="grid grid-cols-[1fr_64px_64px_64px_64px] items-center px-4 pb-2 text-xs font-medium text-[var(--color-text-muted)] border-b border-black/5">
      <span>Permission</span>
      <span className="text-center">Add</span>
      <span className="text-center">Edit</span>
      <span className="text-center">View</span>
      <span className="text-center">Delete</span>
    </div>
  );
}

function PermissionGroupSection({
  group,
  permissions,
  canEdit,
  isAdministratorRole,
  getResults,
  onToggle,
}: {
  group: PermissionGroup;
  permissions: SystemPermission[];
  canEdit: boolean;
  isAdministratorRole: boolean;
  getResults: (p: SystemPermission) => PermissionResults;
  onToggle: (p: SystemPermission, key: 'add' | 'edit' | 'view' | 'delete') => void;
}) {
  return (
    <section>
      <h4 className="text-sm font-medium text-[var(--color-text-default,#151B20)] mb-2 px-4">
        {group.groupName ?? 'Unnamed'}
      </h4>
      <ul>
        {permissions.map((p) => {
          const results = getResults(p);
          const adminLocked =
            isAdministratorRole &&
            !!p.permissionKey &&
            ADMIN_LOCKED_KEYS.has(p.permissionKey);
          return (
            <li
              key={p.id ?? p.permissionKey ?? Math.random()}
              className="grid grid-cols-[1fr_64px_64px_64px_64px] items-center px-4 py-2.5 odd:bg-[var(--color-surface-background)] rounded-md"
            >
              <div className="min-w-0">
                <div className="text-sm text-[var(--color-text-default,#151B20)] truncate">
                  {p.permissionName ?? p.permissionKey ?? 'Unnamed'}
                </div>
              </div>
              <PermissionCheckbox
                checked={!!results.add}
                disabled={!canEdit || adminLocked}
                onChange={() => onToggle(p, 'add')}
                label={`Add ${p.permissionName ?? ''}`}
              />
              <PermissionCheckbox
                checked={!!results.edit}
                disabled={!canEdit || adminLocked}
                onChange={() => onToggle(p, 'edit')}
                label={`Edit ${p.permissionName ?? ''}`}
              />
              <PermissionCheckbox
                checked={!!results.view}
                disabled={!canEdit || adminLocked}
                onChange={() => onToggle(p, 'view')}
                label={`View ${p.permissionName ?? ''}`}
              />
              <PermissionCheckbox
                checked={!!results.delete}
                disabled={!canEdit || adminLocked}
                onChange={() => onToggle(p, 'delete')}
                label={`Delete ${p.permissionName ?? ''}`}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function PermissionCheckbox({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center justify-center cursor-pointer">
      <span className="sr-only">{label}</span>
      <Checkbox checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </label>
  );
}

/**
 * Builds the full Role payload sent to `PUT /api/TenantRoles/{id}`. Critical
 * invariant: existing `permissionResults.id` values (backend row PKs like
 * 37333) MUST survive a save — losing one causes the server to insert a
 * duplicate row and corrupts the role's permission state.
 *
 * Behaviour:
 * - Un-touched permissions are returned by reference (no rebuild), so their
 *   original `permissionResults` (including `.id`) ships unchanged.
 * - Touched permissions rebuild `permissionResults`, but the `id` is taken
 *   from the edit-state map (which was initialised from the original GET).
 *   If the edit-state somehow lacks an id, falls through to the original
 *   `perm.permissionResults?.id`. Final fallback is `null` — used for
 *   permissions activated for the first time (no prior row to update).
 *
 * Pure function — exported so the wire-shape can be unit-tested in
 * `role-edit-dialog.test.ts` without rendering the whole dialog.
 */
export function buildSaveRolePayload(
  roleDetail: Role,
  edits: PermissionEditState,
  roleName: string,
  roleDescription: string,
): Role {
  return {
    ...roleDetail,
    roleName,
    roleDescription,
    permissiongrp:
      roleDetail.permissiongrp?.map((group) => ({
        ...group,
        systemPermission:
          group.systemPermission?.map((perm) => {
            if (perm.id == null) return perm;
            const edited = edits.get(perm.id);
            if (!edited) return perm;
            return {
              ...perm,
              permissionResults: {
                id: edited.id ?? perm.permissionResults?.id ?? null,
                add: edited.add ?? false,
                edit: edited.edit ?? false,
                view: edited.view ?? false,
                delete: edited.delete ?? false,
              },
            };
          }) ?? null,
      })) ?? null,
  };
}

function initialEditsFromRole(role: Role | null): PermissionEditState {
  const map = new Map<number, PermissionResults>();
  if (!role?.permissiongrp) return map;
  for (const group of role.permissiongrp) {
    if (!group.systemPermission) continue;
    for (const perm of group.systemPermission) {
      if (perm.id == null) continue;
      map.set(perm.id, {
        id: perm.permissionResults?.id ?? null,
        view: perm.permissionResults?.view ?? false,
        add: perm.permissionResults?.add ?? false,
        edit: perm.permissionResults?.edit ?? false,
        delete: perm.permissionResults?.delete ?? false,
      });
    }
  }
  return map;
}

function resultsEqual(
  a: PermissionResults | undefined,
  b: PermissionResults | undefined,
): boolean {
  const va = !!a?.view;
  const vb = !!b?.view;
  const aa = !!a?.add;
  const ab = !!b?.add;
  const ea = !!a?.edit;
  const eb = !!b?.edit;
  const da = !!a?.delete;
  const db = !!b?.delete;
  // permissionResults.id must also match — losing it would duplicate-insert on save
  return va === vb && aa === ab && ea === eb && da === db && (a?.id ?? null) === (b?.id ?? null);
}

export type { PermissionGroup };
